import { useState, useMemo } from "react";
import { ChevronDown, LogOut } from "lucide-react";
import { BrandMark } from "../shared/BrandMark";
import { NAV } from "../../lib/rbac";

/* Sidebar — navy background (#162a52) with the gold logo color (#c79a3a)
   marking the active item via a left-edge accent strip + colored text.
   Sections start collapsed; the user expands what they need. If the
   currently-active route lives inside a section, that section auto-expands
   on mount so the highlight is visible immediately. Mobile: slides in
   from the left as an overlay; tap the backdrop to dismiss. */
export function Sidebar({ role, active, setActive, open, setOpen }) {
  // Seed expanded state once. If a deep route is active (e.g. "leads.all")
  // expand its parent section so the active leaf is visible.
  const initialExpanded = useMemo(() => {
    if (!active.includes(".")) return [];
    return [active.split(".")[0]];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [expanded, setExpanded] = useState(initialExpanded);

  const visibleNav = NAV.filter((n) => n.roles.includes(role));
  const toggleExpand = (id) =>
    setExpanded(
      expanded.includes(id) ? expanded.filter((x) => x !== id) : [...expanded, id]
    );

  return (
    <>
      {/* Mobile backdrop */}
      {open && (
        <div
          className="fixed inset-0 bg-black/20 z-30 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 w-60 bg-[#162a52] flex flex-col transition-transform ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        {/* Brand header */}
        <div className="h-14 flex items-center gap-2 px-4 border-b border-white/10">
          <BrandMark />
          <span className="font-semibold text-white text-sm tracking-wider">
            ABHIGHAR
          </span>
          <span className="ml-auto text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#c79a3a]/20 text-[#e6c987] ring-1 ring-[#c79a3a]/30 whitespace-nowrap">
            {role === "super_admin" ? "Super Admin" : role === "admin" ? "Admin" : role === "employee" ? "Employee" : "User"}
          </span>
        </div>

        <nav className="flex-1 overflow-y-auto p-2 space-y-0.5">
          {visibleNav.map((item) => {
            const kids = item.children?.filter((c) => c.roles.includes(role));
            const isExpanded = expanded.includes(item.id);
            const isActive =
              active === item.id || active.startsWith(item.id + ".");
            return (
              <div key={item.id}>
                <button
                  onClick={() =>
                    kids?.length
                      ? toggleExpand(item.id)
                      : (setActive(item.id), setOpen(false))
                  }
                  className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm font-medium transition-colors relative ${
                    isActive && !kids?.length
                      ? "bg-[#c79a3a]/15 text-[#c79a3a]"
                      : "text-slate-300 hover:bg-white/5 hover:text-white"
                  }`}
                >
                  {/* Gold left-edge accent on the active leaf — echoes the
                      gold column in the AbhiGhar logo. */}
                  {isActive && !kids?.length && (
                    <span
                      className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r bg-[#c79a3a]"
                      aria-hidden
                    />
                  )}
                  <item.icon className="w-4 h-4 shrink-0" />
                  <span className="flex-1 text-left">{item.label}</span>
                  {kids?.length > 0 && (
                    <ChevronDown
                      className={`w-3.5 h-3.5 transition-transform ${
                        isExpanded ? "rotate-180" : ""
                      }`}
                    />
                  )}
                </button>
                {kids?.length > 0 && isExpanded && (
                  <div className="ml-4 mt-0.5 space-y-0.5 border-l border-white/10 pl-2">
                    {kids.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => {
                          setActive(c.id);
                          setOpen(false);
                        }}
                        className={`w-full text-left px-2.5 py-1.5 rounded-lg text-sm transition-colors ${
                          active === c.id
                            ? "bg-[#c79a3a]/15 text-[#c79a3a] font-medium"
                            : "text-slate-400 hover:bg-white/5 hover:text-white"
                        }`}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="p-3 border-t border-white/10">
          <button className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-sm text-slate-400 hover:bg-white/5 hover:text-white">
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      </aside>
    </>
  );
}
